import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { clearUserCache, updateProfileCache, updateUserCache } from '../utils/userCache';
import { getCachedProfile, setCachedProfile, clearCachedProfile } from '../utils/profileCache';
import { MAIN_SYSTEM_URL } from '../config';
import { getAllCompanies } from '../api/companies';


const AuthContext = createContext({});

/**
 * 儲存檢視公司設定到 DB (project_profiles.settings)
 * @param {string} userId - 主系統 User ID
 * @param {object} updates - 要更新的設定 { viewing_company_id? }
 */
const saveViewingContextToDB = async (userId, updates) => {
  try {
    // 1. 先取得目前的 settings
    const { data: currentProfile, error: fetchError } = await supabase
      .from('project_profiles')
      .select('settings')
      .eq('user_id', userId)
      .single();

    if (fetchError) {
      console.error('❌ 無法讀取 project_profiles settings:', fetchError);
      return;
    }

    // 2. 合併現有設定與新設定
    const currentSettings = currentProfile?.settings || {};
    const newSettings = {
      ...currentSettings,
      ...updates
    };

    // 3. 更新到 DB
    const { error: updateError } = await supabase
      .from('project_profiles')
      .update({ settings: newSettings })
      .eq('user_id', userId);

    if (updateError) {
      console.error('❌ 無法更新 project_profiles settings:', updateError);
    }
  } catch (error) {
    console.error('❌ saveViewingContextToDB 發生錯誤:', error);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * 同步 project_profiles（確保用戶在 project_profiles 表中有記錄）
 * 遷移後 project_profiles 使用 user_id（= 主系統 profiles.id = auth.uid()）作為關聯
 * @param {string} userId - 主系統的用戶 ID
 * @param {string} email - 用戶 email
 * @returns {Promise<{settings: object}|null>} settings 或 null
 */
async function syncProjectProfile(userId, email) {
  try {
    const { data: existingProfile, error: fetchError } = await supabase
      .from('project_profiles')
      .select('user_id, settings')
      .eq('user_id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('❌ Error fetching project_profile:', fetchError);
      return null;
    }

    if (existingProfile) {
      return { settings: existingProfile.settings || {} };
    }

    // 新用戶，建立 project_profiles 記錄
    console.log('ℹ️ Creating new project_profile for user:', email);
    const { error: insertError } = await supabase
      .from('project_profiles')
      .insert({
        user_id: userId,
        email: email,
      });

    if (insertError) {
      console.error('❌ Error creating project_profile:', insertError);
      return null;
    }

    return { settings: {} };
  } catch (err) {
    console.error('❌ syncProjectProfile error:', err);
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isInitializedRef = useRef(false);
  const [viewingCompanyId, setViewingCompanyId] = useState(undefined);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    checkAuth();

    // 監聽 Supabase auth 狀態變化（取代舊的 30 秒輪詢）
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          console.warn('⚠️ Supabase session 已失效，導回登入');
          setUser(null);
          setProfile(null);
          clearUserCache();
          window.location.href = `${MAIN_SYSTEM_URL}/auth/login`;
          return;
        }

        if (event === 'TOKEN_REFRESHED') {
          console.log('✅ Supabase token 已自動更新');
        }
      }
    );

    // 🔔 跨分頁登出同步：監聽主系統或其他子系統的登出事件
    const handleLogoutEvent = (e) => {
      if (e.key === 'sonice-logout-event' && e.newValue) {
        console.log('🔔 偵測到其他分頁登出');
        setUser(null);
        setProfile(null);
        clearCachedProfile();
        window.location.href = `${MAIN_SYSTEM_URL}/auth/login`;
      }
    };
    window.addEventListener('storage', handleLogoutEvent);

    // 🔍 分頁切回時驗證 session（避免用過期 session 操作）
    let lastSessionCheck = 0;
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastSessionCheck < 30000) return; // 30 秒節流
      lastSessionCheck = now;
      const { error } = await supabase.auth.getUser();
      if (error) {
        console.log('🔒 Session 已失效（分頁切回檢測）');
        clearCachedProfile();
        window.location.href = `${MAIN_SYSTEM_URL}/auth/login`;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('storage', handleLogoutEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);

      // 1. 用 getSession 讀 cache（不打網路，不會卡住）
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        console.warn('未登入或 session 已失效，導回主系統登入');
        window.location.href = `${MAIN_SYSTEM_URL}/auth/login`;
        return;
      }

      const authUser = session.user;

      // 2. 嘗試從跨子系統共用 cache 取得基本 profile
      const cached = getCachedProfile();
      if (cached && cached.id === authUser.id) {
        // Cache 命中：先用 cache 資料讓 UI 立即可用
        const cachedUserObject = {
          id: authUser.id,
          email: authUser.email,
          user_metadata: { name: cached.name },
        };
        setUser(cachedUserObject);
        updateUserCache(cachedUserObject);

        // 同步 project_profiles（PMS 專屬，不在共用 cache 中）
        const syncResult = await syncProjectProfile(cached.id, cached.email);

        const cachedProfileObj = {
          id: cached.id,
          email: cached.email,
          name: cached.name,
          avatar_url: cached.avatar_url,
          company_id: cached.company_id,
          department_id: cached.department_id,
          role: cached.role,
          settings: syncResult?.settings || {},
          department: null,
          _source: 'cache',
        };

        setProfile(cachedProfileObj);
        updateProfileCache(cachedProfileObj);
        setLoading(false);

        // 初始化 viewingCompanyId（使用 cached 資料）
        await initializeViewingContext(cachedProfileObj);

        // 背景刷新：從 DB 取最新 profile，若有變更則更新
        supabase
          .from('profiles')
          .select('id, email, name, avatar_url, role, company_id, department_id')
          .eq('id', authUser.id)
          .single()
          .then(({ data: freshData, error: freshError }) => {
            if (freshError || !freshData) return;

            // 比對是否有變更
            const hasChanged =
              freshData.name !== cached.name ||
              freshData.avatar_url !== cached.avatar_url ||
              freshData.role !== cached.role ||
              freshData.company_id !== cached.company_id ||
              freshData.department_id !== cached.department_id;

            if (hasChanged) {
              const freshProfileObj = {
                id: freshData.id,
                email: freshData.email,
                name: freshData.name,
                avatar_url: freshData.avatar_url,
                company_id: freshData.company_id,
                department_id: freshData.department_id,
                role: freshData.role,
                settings: syncResult?.settings || {},
                department: null,
                _source: 'supabase',
              };

              setProfile(freshProfileObj);
              updateProfileCache(freshProfileObj);

              // 更新共用 cache
              setCachedProfile({
                id: freshData.id,
                email: freshData.email,
                name: freshData.name,
                avatar_url: freshData.avatar_url,
                role: freshData.role,
                company_id: freshData.company_id,
                department_id: freshData.department_id,
              });

              // 角色或公司變更時重新初始化 viewingContext
              if (freshData.role !== cached.role || freshData.company_id !== cached.company_id) {
                initializeViewingContext(freshProfileObj);
              }

              const freshUserObject = {
                id: authUser.id,
                email: authUser.email,
                user_metadata: { name: freshData.name },
              };
              setUser(freshUserObject);
              updateUserCache(freshUserObject);
            }
          });

        return;
      }

      // 3. 無 cache：從 profiles 表取得用戶完整資訊
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, name, avatar_url, role, company_id, department_id')
        .eq('id', authUser.id)
        .single();

      if (profileError || !profileData) {
        console.error('❌ 無法取得 profile:', profileError);
        throw new Error('無法取得用戶資料');
      }

      // 寫入跨子系統共用 cache
      setCachedProfile({
        id: profileData.id,
        email: profileData.email,
        name: profileData.name,
        avatar_url: profileData.avatar_url,
        role: profileData.role,
        company_id: profileData.company_id,
        department_id: profileData.department_id,
      });

      // 4. 同步 project_profiles（確保有記錄，取回 settings）
      const syncResult = await syncProjectProfile(profileData.id, profileData.email);

      // 5. 設定用戶資料
      const userObject = {
        id: authUser.id,
        email: authUser.email,
        user_metadata: { name: profileData.name },
      };
      setUser(userObject);

      const profileObj = {
        id: profileData.id,
        email: profileData.email,
        name: profileData.name,
        avatar_url: profileData.avatar_url,
        company_id: profileData.company_id,
        department_id: profileData.department_id,
        role: profileData.role,
        settings: syncResult?.settings || {},
        department: null,
        _source: 'supabase',
      };

      setProfile(profileObj);

      // 更新 userCache
      updateUserCache(userObject);
      updateProfileCache(profileObj);

      // 初始化 viewingCompanyId
      await initializeViewingContext(profileObj);
    } catch (error) {
      console.error('認證錯誤:', error);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // 初始化檢視公司
  const initializeViewingContext = async (profileData) => {
    if (profileData?.role === 'super_admin') {
      // Super Admin: 從 DB settings 讀取或載入第一個公司
      const savedSettings = profileData.settings || {};
      const savedCompany = savedSettings.viewing_company_id;

      const isValidCompanyUUID = savedCompany && typeof savedCompany === 'string' && savedCompany.length === 36;

      try {
        const { data: companies } = await getAllCompanies();
        if (companies && companies.length > 0) {
          const savedExists = isValidCompanyUUID && companies.some(c => c.id === savedCompany);

          if (savedExists) {
            setViewingCompanyId(savedCompany);
          } else {
            setViewingCompanyId(companies[0].id);
            await saveViewingContextToDB(profileData.id, { viewing_company_id: companies[0].id });
          }
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    } else {
      // Company Admin / Department Admin / Member: 使用實際公司
      setViewingCompanyId(profileData.company_id);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      clearUserCache();
      clearCachedProfile();

      // 先登出 Supabase session
      await supabase.auth.signOut();

      // 🔔 通知其他分頁
      localStorage.setItem('sonice-logout-event', Date.now().toString());
      setTimeout(() => localStorage.removeItem('sonice-logout-event'), 100);

      // 跳轉到主系統登出頁面（主系統負責清除其他 cookie）
      window.location.href = `${MAIN_SYSTEM_URL}/auth/logout`;
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return profile?.role === 'company_admin' || profile?.role === 'department_admin' || profile?.role === 'super_admin';
  };

  const isSuperAdmin = () => {
    return profile?.role === 'super_admin';
  };

  const isCompanyAdmin = () => {
    return profile?.role === 'company_admin' || profile?.role === 'super_admin';
  };

  const isDepartmentAdmin = () => {
    return profile?.role === 'department_admin' || profile?.role === 'company_admin' || profile?.role === 'super_admin';
  };

  const isMember = () => {
    return profile?.role === 'member' || profile?.role === 'department_admin' || profile?.role === 'company_admin' || profile?.role === 'super_admin';
  };

  const refreshProfile = async () => {
    if (user?.id) {
      // 從 profiles 表重新讀取用戶資料
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, name, avatar_url, role, company_id, department_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('❌ refreshProfile 失敗:', profileError);
        return;
      }

      // 取得 project_profiles settings
      const { data: pp } = await supabase
        .from('project_profiles')
        .select('settings')
        .eq('user_id', user.id)
        .single();

      const updatedProfile = {
        id: profileData.id,
        email: profileData.email,
        name: profileData.name,
        avatar_url: profileData.avatar_url,
        company_id: profileData.company_id,
        department_id: profileData.department_id,
        role: profileData.role,
        settings: pp?.settings || profile?.settings || {},
        department: null,
        _source: 'supabase',
      };

      setProfile(updatedProfile);
      updateProfileCache(updatedProfile);
    }
  };

  // 局部更新 profile.settings（僅本地狀態，不寫 DB）
  const patchProfileSettings = (updates) => {
    setProfile(prev => prev ? {
      ...prev,
      settings: { ...(prev.settings || {}), ...updates },
    } : prev);
  };

  // 切換檢視公司（只有 Super Admin 可用）
  const setViewingCompany = async (companyId) => {
    if (profile?.role !== 'super_admin') {
      console.warn('Only super_admin can switch viewing company');
      return;
    }

    setViewingCompanyId(companyId);
    await saveViewingContextToDB(profile.id, { viewing_company_id: companyId });
  };

  // 取得有效的公司 ID（用於資料過濾）
  const getEffectiveCompanyId = () => {
    if (profile?.role === 'super_admin') {
      return viewingCompanyId;
    }
    return profile?.company_id;
  };

  const value = {
    user,
    profile,
    loading,
    viewingCompanyId,
    signOut,
    refreshProfile,
    patchProfileSettings,
    isAdmin,
    isSuperAdmin,
    isCompanyAdmin,
    isDepartmentAdmin,
    isMember,
    setViewingCompany,
    getEffectiveCompanyId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
